import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  Chip,
} from '@mui/joy';
import {
  Sparkle as AIIcon,
  Image as ImageIcon,
  PencilSimple as EditIcon,
  Megaphone as CampaignIcon,
  Gauge as SpeedIcon,
  MagnifyingGlass as SEOIcon,
} from '@phosphor-icons/react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api';

interface QuickActionsProps {
  siteId: number;
  onActionComplete?: () => void;
}

export const QuickActions: React.FC<QuickActionsProps> = ({ siteId, onActionComplete }) => {
  // AI Content Refresh
  const refreshContentMutation = useMutation({
    mutationFn: async () => {
      return api.post(`/api/cms/sites/${siteId}/refresh-all-content/`, {});
    },
    onSuccess: () => {
      onActionComplete?.();
    },
  });

  // Import Latest Photos
  const importPhotosMutation = useMutation({
    mutationFn: async () => {
      return api.post('/api/cms/media/import-latest/', { site: siteId });
    },
    onSuccess: () => {
      onActionComplete?.();
    },
  });

  // Create Special Offer Page
  const createOfferMutation = useMutation({
    mutationFn: async () => {
      return api.post(`/api/cms/sites/${siteId}/create-offer-page/`, {
        title: 'Limited Time Offer',
        offer: '20% OFF All Services This Month',
      });
    },
    onSuccess: () => {
      onActionComplete?.();
    },
  });

  // Run SEO Audit
  const seoAuditMutation = useMutation({
    mutationFn: async () => {
      return api.post(`/api/cms/sites/${siteId}/seo-audit/`, {});
    },
  });

  // Optimize Images
  const optimizeImagesMutation = useMutation({
    mutationFn: async () => {
      return api.post(`/api/cms/sites/${siteId}/optimize-all-images/`, {});
    },
  });

  const actions = [
    {
      title: 'Refresh All Content',
      description: 'Update all AI-generated content with latest info',
      icon: <AIIcon />,
      color: 'primary',
      onClick: () => refreshContentMutation.mutate(),
      loading: refreshContentMutation.isPending,
      chip: 'AI Powered',
    },
    {
      title: 'Import Latest Photos',
      description: 'Add new marketing photos from recent jobs',
      icon: <ImageIcon />,
      color: 'success',
      onClick: () => importPhotosMutation.mutate(),
      loading: importPhotosMutation.isPending,
      chip: 'Auto Import',
    },
    {
      title: 'Create Special Offer',
      description: 'Quick page for seasonal promotions',
      icon: <CampaignIcon />,
      color: 'warning',
      onClick: () => createOfferMutation.mutate(),
      loading: createOfferMutation.isPending,
      chip: 'Boost Sales',
    },
    {
      title: 'Update Service Areas',
      description: 'Expand or modify coverage zones',
      icon: <EditIcon />,
      color: 'neutral',
      onClick: () => window.location.href = `/sites/${siteId}/settings#service-areas`,
      chip: 'Location SEO',
    },
    {
      title: 'Run SEO Check',
      description: 'Analyze and fix SEO issues',
      icon: <SEOIcon />,
      color: 'info',
      onClick: () => seoAuditMutation.mutate(),
      loading: seoAuditMutation.isPending,
      chip: 'SEO Health',
    },
    {
      title: 'Optimize Images',
      description: 'Compress and enhance all photos',
      icon: <SpeedIcon />,
      color: 'success',
      onClick: () => optimizeImagesMutation.mutate(),
      loading: optimizeImagesMutation.isPending,
      chip: 'Performance',
    },
  ];

  return (
    <Box>
      <Typography level="h3" sx={{ mb: 3 }}>
        Quick Actions
      </Typography>
      
      <Grid container spacing={2}>
        {actions.map((action, index) => (
          <Grid xs={12} sm={6} md={4} key={index}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: 'md',
                  borderColor: `${action.color}.500`,
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: `${action.color}.100`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: `${action.color}.600`,
                      mr: 2,
                    }}
                  >
                    {action.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography level="title-md">{action.title}</Typography>
                    {action.chip && (
                      <Chip size="sm" variant="soft" color={action.color as any}>
                        {action.chip}
                      </Chip>
                    )}
                  </Box>
                </Box>
                
                <Typography level="body-sm" sx={{ mb: 2 }}>
                  {action.description}
                </Typography>
                
                <Button
                  fullWidth
                  variant="soft"
                  color={action.color as any}
                  onClick={action.onClick}
                  loading={action.loading}
                >
                  {action.loading ? 'Processing...' : 'Run Now'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {seoAuditMutation.isSuccess && (
        <Alert sx={{ mt: 3 }} color="success">
          SEO audit complete! Check your email for the full report.
        </Alert>
      )}
    </Box>
  );
};
